/**
 * Binding: the JavaScript side of the Rust compute scheduler. It keeps each job's JavaScript
 * values (the `run` function, the caller's promise, an `AbortController`, back-off listeners)
 * keyed by the job id the Rust core assigns, and carries out the core's actions:
 *
 * - start (1): call `run(context)`, then report the result with `core.complete(id, ok)`;
 * - abort (2): reject the caller with a `ComputeAbortError` and fire the job's AbortSignal;
 * - back-off (3): call the job's back-off listeners.
 *
 * Every decision (lanes, joins and supersession, deadlines, limits, dispatch order, back-off)
 * is made in `crates/compute-scheduler`; read its Rustdoc for the model and its guarantees.
 *
 * Before the WASM package has loaded, submissions wait for it (a few milliseconds after page
 * start). If it cannot load (an old browser, a build without the package, or a non-browser test
 * runtime), the scheduler runs in *passthrough*: each job starts as soon as its resource group has
 * room (jobs of one Worker run one at a time, as each Worker client requires), honouring only its
 * caller signal and deadline. That keeps the app working, and is exactly the behaviour the app
 * had before the scheduler existed, including the planning Workers' own one-at-a-time queues.
 *
 * @module apps/web/src/lib/compute/scheduler
 */

import { yieldToMain, LANE_PRIORITY } from './mainThread';
import {
  ComputeAbortError,
  type ComputeAbortReason,
  type ComputeAdapter,
  type ComputeBackoffReason,
  type ComputeJobContext,
  type ComputeJobHandle,
  type ComputeJobOptions,
  type ComputeJobState,
  type ComputeLane,
  type ComputePrewarmOptions,
  type ComputeSubmitOptions,
  type RegisteredCompute,
} from './types';
import type { ComputeCoreHandle, ComputeModule } from './wasm';

/** Abort reason names by Rust code. */
const ABORT_REASONS: Record<number, ComputeAbortReason> = {
  1: 'cancelled',
  2: 'superseded',
  3: 'deadline',
  4: 'reset',
};
/** Rust code of each abort reason. */
const ABORT_CODES: Record<ComputeAbortReason, number> = {
  cancelled: 1,
  superseded: 2,
  deadline: 3,
  reset: 4,
};
/** Back-off reason names by Rust code (0 = none). */
const BACKOFF_REASONS: ComputeBackoffReason[] = [
  null,
  'input',
  'hidden',
  'save-data',
  'battery',
  'foreground',
];

/** JavaScript values of one job the core knows by id. */
interface JobRecord {
  /** Core id. */
  id: number;
  /** Telemetry label. */
  name: string;
  /** Lane at submission. */
  lane: ComputeLane;
  /** The work. */
  run: (context: ComputeJobContext) => unknown;
  /** Resolve the caller. */
  resolve: (value: unknown) => void;
  /** Reject the caller. */
  reject: (error: unknown) => void;
  /** Aborted on every scheduler stop. */
  controller: AbortController;
  /** Back-off subscribers. */
  listeners: Set<(reason: ComputeBackoffReason) => void>;
  /** Lifecycle as seen by the caller. */
  state: ComputeJobState;
  /** Handle given to callers and joiners. */
  handle: ComputeJobHandle<unknown>;
}

/** Overrides passed to the Rust core at attach time (tests only). */
export interface ComputeOverrides {
  /** Replaces the 30 s background start delay. */
  backgroundStartDelayMs?: number;
  /** Replaces the 1 s input-quiet window. */
  inputQuietMs?: number;
}

/** The page's scheduler binding; see the module comment. */
export class ComputeScheduler {
  /** Rust core once attached. */
  private core?: ComputeCoreHandle;
  /** `loading` until attach/fallBack, then `ready` or `passthrough`. */
  private mode: 'loading' | 'ready' | 'passthrough' = 'loading';
  /** Submissions waiting for the core to load. */
  private waiting: Array<() => void> = [];
  /** Job records by core id. */
  private readonly records = new Map<number, JobRecord>();
  /** Group limits, replayed into the core when it attaches. */
  private readonly groupLimits = new Map<string, number>();
  /** Registered adapter names. */
  private readonly adapters = new Set<string>();
  /** Re-entrancy guard of `drain`. */
  private draining = false;
  /** Set by a nested `drain` so the running one loops again. */
  private redrain = false;
  /** Passthrough only: running jobs and waiting starts per resource group. */
  private readonly passthroughGroups = new Map<
    string,
    {
      /** Jobs of the group whose work has not reported back. */ running: number;
      /** Starts waiting for a free slot, oldest first. */ waiting: Array<() => void>;
    }
  >();

  /** Attach the loaded Rust package; flushes waiting submissions. */
  attach(module: ComputeModule, overrides: ComputeOverrides = {}): void {
    if (this.mode !== 'loading') return;
    this.core = new module.ComputeCore(
      () => this.drain(),
      overrides.backgroundStartDelayMs,
      overrides.inputQuietMs
    );
    for (const [group, limit] of this.groupLimits) this.core.setGroupLimit(group, limit);
    this.mode = 'ready';
    this.flush();
  }

  /** Switch to passthrough because the core is unavailable; flushes waiting submissions. */
  fallBack(): void {
    if (this.mode !== 'loading') return;
    this.mode = 'passthrough';
    this.flush();
  }

  /** The attached Rust core, if any (for the responsiveness binding and benchmarks). */
  get rustCore(): ComputeCoreHandle | undefined {
    return this.core;
  }

  /** Whether jobs are scheduled by the Rust core (`ready`), waiting, or passed through. */
  get status(): 'loading' | 'ready' | 'passthrough' {
    return this.mode;
  }

  /** Submit a job; see `ComputeJobOptions` and the Rust `scheduler` module. */
  schedule<T>(options: ComputeJobOptions<T>): ComputeJobHandle<T> {
    if (this.mode === 'ready') return this.submitToCore(options);
    if (this.mode === 'passthrough') return this.passthroughInGroup(options);
    return this.deferred(options);
  }

  /** Convenience: `schedule(options).promise`. */
  run<T>(options: ComputeJobOptions<T>): Promise<T> {
    return this.schedule(options).promise;
  }

  /** Set a resource group's concurrency limit. */
  setGroupLimit(group: string, limit: number): void {
    this.groupLimits.set(group, limit);
    this.core?.setGroupLimit(group, limit);
  }

  /** Telemetry snapshot (parsed Rust JSON), or null without the core. */
  snapshot(): unknown {
    return this.core ? JSON.parse(this.core.snapshot()) : null;
  }

  /** Whether a chunked main-thread loop should yield (Rust policy; 8 ms without the core). */
  shouldYield(sliceElapsedMs: number): boolean {
    return this.core ? this.core.shouldYield(sliceElapsedMs) : sliceElapsedMs >= 8;
  }

  /**
   * Register a Worker adapter: its submissions become jobs of group `adapter.name` (limit
   * `maxConcurrency`, default 1) with keys namespaced by adapter.
   */
  register<Request, Response>(
    adapter: ComputeAdapter<Request, Response>
  ): RegisteredCompute<Request, Response> {
    if (this.adapters.has(adapter.name))
      throw new Error(`Compute adapter ${adapter.name} is already registered.`);
    this.adapters.add(adapter.name);
    this.setGroupLimit(adapter.name, adapter.maxConcurrency ?? 1);
    let registered = true;
    return {
      name: adapter.name,
      submit: (request: Request, options: ComputeSubmitOptions = {}) =>
        registered
          ? this.run<Response>({
              name: adapter.name,
              lane: options.lane ?? adapter.lane,
              key: options.key === undefined ? undefined : `${adapter.name}:${options.key}`,
              dedupe: options.dedupe,
              deadlineMs: options.deadlineMs ?? adapter.deadlineMs,
              estimatedMs: options.estimatedMs ?? adapter.estimatedMs,
              group: adapter.name,
              signal: options.signal,
              notBefore: options.notBefore,
              run: context => adapter.execute(request, context.signal, context),
            })
          : Promise.reject(new ComputeAbortError('reset', adapter.name)),
      prewarm: (prewarmOptions: ComputePrewarmOptions = {}) => {
        if (!registered || !adapter.prewarm) return;
        // Same group as the adapter's requests: a request arriving meanwhile waits for the warm
        // Worker. Same key, joined: a more urgent call promotes the warm-up (Rust `merge`).
        this.run<void>({
          name: `${adapter.name}:prewarm`,
          lane: prewarmOptions.lane ?? 'background',
          key: `${adapter.name}:prewarm`,
          dedupe: 'join',
          group: adapter.name,
          run: context => adapter.prewarm!(context.signal),
        }).catch(() => undefined);
      },
      cancelAll: () => {
        this.core?.cancelAll(adapter.name);
        this.core?.cancelAll(`${adapter.name}:prewarm`);
        this.drain();
      },
      unregister: (reason = `${adapter.name} was reset.`) => {
        if (!registered) return;
        registered = false;
        this.core?.cancelAll(adapter.name);
        this.drain();
        adapter.reset?.(reason);
        this.adapters.delete(adapter.name);
      },
    };
  }

  /**
   * Passthrough with the one rule every Worker client needs: at most the group's limit of jobs of
   * one resource group run at once; others start in arrival order as slots free. A slot is freed
   * when the work reports back, not when the caller is rejected, as in the Rust core.
   */
  private passthroughInGroup<T>(options: ComputeJobOptions<T>): ComputeJobHandle<T> {
    const limit = options.group === undefined ? undefined : this.groupLimits.get(options.group);
    if (options.group === undefined || limit === undefined) return passthrough(options);
    let slots = this.passthroughGroups.get(options.group);
    if (!slots) this.passthroughGroups.set(options.group, (slots = { running: 0, waiting: [] }));
    const group = slots;
    const release = () => {
      group.running -= 1;
      group.waiting.shift()?.();
    };
    const begin = (): ComputeJobHandle<T> => {
      group.running += 1;
      return passthrough({
        ...options,
        run: context => {
          let value: Promise<T> | T;
          try {
            value = options.run(context);
          } catch (error) {
            release();
            throw error;
          }
          Promise.resolve(value).then(release, release);
          return value;
        },
      });
    };
    if (group.running < limit) return begin();
    let inner: ComputeJobHandle<T> | undefined;
    let cancelled: ComputeAbortReason | undefined;
    let resolveInner!: (handle: ComputeJobHandle<T>) => void;
    const ready = new Promise<ComputeJobHandle<T>>(resolve => (resolveInner = resolve));
    const start = () => {
      if (cancelled || options.signal?.aborted) {
        resolveInner(rejected(options.name, cancelled ?? 'cancelled') as ComputeJobHandle<T>);
        group.waiting.shift()?.();
        return;
      }
      inner = begin();
      resolveInner(inner);
    };
    group.waiting.push(start);
    const promise = ready.then(handle => handle.promise);
    promise.catch(() => undefined);
    const handle: ComputeJobHandle<T> = {
      id: 0,
      promise,
      get state() {
        return inner?.state ?? (cancelled ? 'settled' : 'queued');
      },
      cancel(reason: ComputeAbortReason = 'cancelled') {
        if (inner) return inner.cancel(reason);
        if (cancelled) return;
        cancelled = reason;
        const index = group.waiting.indexOf(start);
        if (index >= 0) group.waiting.splice(index, 1);
        resolveInner(rejected(options.name, reason) as ComputeJobHandle<T>);
      },
    };
    options.signal?.addEventListener('abort', () => handle.cancel('cancelled'), { once: true });
    return handle;
  }

  /** Run every submission that arrived while loading. */
  private flush(): void {
    for (const submit of this.waiting.splice(0)) submit();
  }

  /** Submit to the Rust core and keep the JavaScript side of the job. */
  private submitToCore<T>(options: ComputeJobOptions<T>): ComputeJobHandle<T> {
    const core = this.core!;
    if (options.signal?.aborted) return rejected(options.name, 'cancelled') as ComputeJobHandle<T>;
    const id = core.submit(
      JSON.stringify({
        name: options.name,
        lane: options.lane,
        key: options.key,
        dedupe: options.dedupe,
        deadlineMs: options.deadlineMs,
        group: options.group,
        notBefore: options.notBefore,
        estimatedMs: options.estimatedMs,
      })
    );
    const joined = this.records.get(id);
    if (joined) {
      this.drain();
      return joined.handle as ComputeJobHandle<T>;
    }
    let resolve!: (value: unknown) => void;
    let reject!: (error: unknown) => void;
    const promise = new Promise<T>((onValue, onError) => {
      resolve = onValue as (value: unknown) => void;
      reject = onError;
    });
    const record: JobRecord = {
      id,
      name: options.name,
      lane: options.lane,
      run: options.run as (context: ComputeJobContext) => unknown,
      resolve,
      reject,
      controller: new AbortController(),
      listeners: new Set(),
      state: 'queued',
      handle: undefined as unknown as ComputeJobHandle<unknown>,
    };
    record.handle = {
      id,
      promise: promise as Promise<unknown>,
      get state() {
        return record.state;
      },
      cancel: (reason: ComputeAbortReason = 'cancelled') => {
        if (record.state === 'settled') return;
        core.cancel(id, ABORT_CODES[reason]);
        this.drain();
      },
    };
    this.records.set(id, record);
    options.signal?.addEventListener('abort', () => record.handle.cancel('cancelled'), {
      once: true,
    });
    this.drain();
    return record.handle as ComputeJobHandle<T>;
  }

  /** Carry out the core's pending actions (re-entrancy safe). */
  private drain(): void {
    if (!this.core) return;
    if (this.draining) {
      this.redrain = true;
      return;
    }
    this.draining = true;
    try {
      do {
        this.redrain = false;
        const actions = this.core.takeActions();
        for (let index = 0; index + 2 < actions.length; index += 3) {
          const record = this.records.get(actions[index + 1]);
          if (!record) continue;
          const kind = actions[index];
          const argument = actions[index + 2];
          if (kind === 1) this.start(record);
          else if (kind === 2) this.abort(record, ABORT_REASONS[argument] ?? 'cancelled');
          else if (kind === 3)
            for (const listener of [...record.listeners])
              listener(BACKOFF_REASONS[argument] ?? null);
        }
      } while (this.redrain);
    } finally {
      this.draining = false;
    }
  }

  /** Answer the caller with an abort and fire the job's signal. */
  private abort(record: JobRecord, reason: ComputeAbortReason): void {
    if (record.state === 'settled') return;
    const wasQueued = record.state === 'queued';
    record.state = 'settled';
    const error = new ComputeAbortError(reason, record.name);
    record.reject(error);
    record.controller.abort(error);
    // A queued job never ran, so no completion will release it; a running one is kept until
    // its work reports back.
    if (wasQueued) this.records.delete(record.id);
  }

  /** Call `run` and report its outcome to the core. */
  private start(record: JobRecord): void {
    const core = this.core!;
    record.state = 'running';
    const context: ComputeJobContext = {
      signal: record.controller.signal,
      lane: record.lane,
      name: record.name,
      backoff: () => BACKOFF_REASONS[core.backoffFor(record.id)] ?? null,
      onBackoffChange: listener => {
        record.listeners.add(listener);
        return () => record.listeners.delete(listener);
      },
      yield: async () => {
        await yieldToMain(LANE_PRIORITY[record.lane]);
        if (record.controller.signal.aborted) throw record.controller.signal.reason;
      },
    };
    let work: Promise<unknown>;
    try {
      work = Promise.resolve(record.run(context));
    } catch (error) {
      work = Promise.reject(error);
    }
    const finish = (ok: boolean, value: unknown) => {
      if (record.state !== 'settled') {
        record.state = 'settled';
        if (ok) record.resolve(value);
        else record.reject(value);
      }
      record.listeners.clear();
      this.records.delete(record.id);
      core.complete(record.id, ok);
      this.drain();
    };
    work.then(
      value => finish(true, value),
      error => finish(false, error)
    );
  }

  /** A handle whose submission waits for the core (or the passthrough decision). */
  private deferred<T>(options: ComputeJobOptions<T>): ComputeJobHandle<T> {
    let inner: ComputeJobHandle<T> | undefined;
    let cancelled: ComputeAbortReason | undefined;
    let resolveInner!: (handle: ComputeJobHandle<T>) => void;
    const ready = new Promise<ComputeJobHandle<T>>(resolve => (resolveInner = resolve));
    this.waiting.push(() => {
      inner = cancelled
        ? (rejected(options.name, cancelled) as ComputeJobHandle<T>)
        : this.schedule(options);
      resolveInner(inner);
    });
    return {
      id: 0,
      promise: ready.then(handle => handle.promise),
      get state() {
        return inner?.state ?? (cancelled ? 'settled' : 'queued');
      },
      cancel(reason: ComputeAbortReason = 'cancelled') {
        if (inner) inner.cancel(reason);
        else cancelled = reason;
      },
    };
  }
}

/** A settled handle that rejects with `reason`. */
function rejected(name: string, reason: ComputeAbortReason): ComputeJobHandle<never> {
  const promise = Promise.reject(new ComputeAbortError(reason, name));
  promise.catch(() => undefined);
  return { id: 0, promise, state: 'settled', cancel: () => undefined };
}

/**
 * Passthrough: start now, honour only the caller signal and deadline. Used when the Rust core is
 * unavailable; no lanes, limits, joins or back-off.
 */
function passthrough<T>(options: ComputeJobOptions<T>): ComputeJobHandle<T> {
  if (options.signal?.aborted) return rejected(options.name, 'cancelled') as ComputeJobHandle<T>;
  const controller = new AbortController();
  let state: ComputeJobState = 'running';
  let settle!: (ok: boolean, value: unknown) => void;
  const promise = new Promise<T>((resolve, reject) => {
    settle = (ok, value) => {
      if (state === 'settled') return;
      state = 'settled';
      clearTimeout(deadline);
      options.signal?.removeEventListener('abort', onCallerAbort);
      if (ok) resolve(value as T);
      else reject(value);
    };
  });
  const stop = (reason: ComputeAbortReason) => {
    if (state === 'settled') return;
    const error = new ComputeAbortError(reason, options.name);
    settle(false, error);
    controller.abort(error);
  };
  const onCallerAbort = () => stop('cancelled');
  options.signal?.addEventListener('abort', onCallerAbort, { once: true });
  const deadline =
    options.deadlineMs === undefined
      ? undefined
      : setTimeout(() => stop('deadline'), options.deadlineMs);
  const context: ComputeJobContext = {
    signal: controller.signal,
    lane: options.lane,
    name: options.name,
    backoff: () => null,
    onBackoffChange: () => () => undefined,
    yield: async () => {
      await yieldToMain(LANE_PRIORITY[options.lane]);
      if (controller.signal.aborted) throw controller.signal.reason;
    },
  };
  try {
    Promise.resolve(options.run(context)).then(
      value => settle(true, value),
      error => settle(false, error)
    );
  } catch (error) {
    settle(false, error);
  }
  return {
    id: 0,
    promise,
    get state() {
      return state;
    },
    cancel: (reason: ComputeAbortReason = 'cancelled') => stop(reason),
  };
}
